import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

// Import settings subpages
import ClinicSettings from './settings/ClinicSettings';
import DoctorSettings from './settings/DoctorSettings';
import UserSettings from './settings/UserSettings';
import TreatmentCodes from './settings/TreatmentCodes';
import ChartOfAccounts from './settings/ChartOfAccounts';

const { FiSettings, FiHome, FiUsers, FiUserPlus, FiList, FiBookOpen } = FiIcons;

const Settings = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('clinic');

  // If user is not admin, redirect to dashboard
  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Update active tab based on URL
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/settings/doctors')) {
      setActiveTab('doctors');
    } else if (path.includes('/settings/users')) {
      setActiveTab('users');
    } else if (path.includes('/settings/treatments')) {
      setActiveTab('treatments');
    } else if (path.includes('/settings/accounts')) {
      setActiveTab('accounts');
    } else {
      setActiveTab('clinic');
    }
  }, [location]);

  // Navigate to appropriate settings tab on first load
  useEffect(() => {
    if (location.pathname === '/settings') {
      navigate('/settings/clinic');
    }
  }, [location.pathname, navigate]);

  const settingsTabs = [
    { id: 'clinic', name: 'Clinic Settings', icon: FiHome, path: '/settings/clinic' },
    { id: 'doctors', name: 'Doctor Settings', icon: FiUserPlus, path: '/settings/doctors' },
    { id: 'users', name: 'User Management', icon: FiUsers, path: '/settings/users' },
    { id: 'treatments', name: 'Treatment Codes', icon: FiList, path: '/settings/treatments' },
    { id: 'accounts', name: 'Chart of Accounts', icon: FiBookOpen, path: '/settings/accounts' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <SafeIcon icon={FiSettings} className="w-6 h-6 text-grey-500 mr-2" />
          <h1 className="text-2xl font-bold text-grey-900">Settings</h1>
        </div>
      </div>

      {/* Settings Navigation */}
      <div className="bg-white shadow rounded-lg">
        <div className="border-b border-grey-200">
          <nav className="flex overflow-x-auto" aria-label="Settings tabs">
            {settingsTabs.map((tab) => (
              <Link
                key={tab.id}
                to={tab.path}
                className={`whitespace-nowrap py-4 px-6 text-sm font-medium border-b-2 ${
                  activeTab === tab.id
                    ? 'border-maroon-500 text-maroon-600'
                    : 'border-transparent text-grey-500 hover:text-grey-700 hover:border-grey-300'
                }`}
                aria-current={activeTab === tab.id ? 'page' : undefined}
              >
                <div className="flex items-center">
                  <SafeIcon icon={tab.icon} className="w-5 h-5 mr-2" />
                  {tab.name}
                </div>
              </Link>
            ))}
          </nav>
        </div>

        {/* Settings Content */}
        <div className="p-6">
          <Routes>
            <Route path="/" element={<ClinicSettings />} />
            <Route path="/clinic" element={<ClinicSettings />} />
            <Route path="/doctors" element={<DoctorSettings />} />
            <Route path="/doctors/:id" element={<DoctorSettings />} />
            <Route path="/users" element={<UserSettings />} />
            <Route path="/treatments" element={<TreatmentCodes />} />
            <Route path="/accounts" element={<ChartOfAccounts />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default Settings;